import { Input, Modal, message } from 'antd';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { inviteCodeStore } from '@/stores';
import type { BatchSendResult } from '@/types';

interface BatchSendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_BATCH = 30;

export function BatchSendDialog({ open, onOpenChange }: BatchSendDialogProps) {
  const [raw, setRaw] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<BatchSendResult[] | null>(null);

  const parseEmails = (text: string): string[] => {
    const list = text
      .split(/[\n,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return Array.from(new Set(list));
  };

  const handleSubmit = async () => {
    if (loading) return;
    const emails = parseEmails(raw);
    if (emails.length === 0) {
      message.error('请输入至少一个邮箱');
      return;
    }
    if (emails.length > MAX_BATCH) {
      message.error(`单次最多发送 ${MAX_BATCH} 个邮箱`);
      return;
    }
    const invalid = emails.filter((e) => !EMAIL_RE.test(e));
    if (invalid.length > 0) {
      message.error(`存在无效邮箱：${invalid.slice(0, 3).join(', ')}`);
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const reply = await inviteCodeStore.batchSendInviteCodes(emails);
      setResults(reply.results);
      message.success(
        `发送完成：成功 ${reply.sent_count} / 失败 ${reply.failed_count}`,
      );
    } catch (error) {
      console.error('批量发送失败:', error);
      message.error('批量发送失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setRaw('');
    setResults(null);
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="批量发送邀请码"
      closable={!loading}
      maskClosable={!loading}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            关闭
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? '发送中...' : '发送'}
          </Button>
        </div>
      }
      centered
      width={560}
    >
      <div className="space-y-4 py-2">
        <div className="space-y-2">
          <Label htmlFor="batch-emails">
            收件邮箱（每行一个，或用逗号/空格分隔，最多 {MAX_BATCH} 个）
          </Label>
          <Input.TextArea
            id="batch-emails"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={'a@example.com\nb@example.com'}
            rows={6}
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground">
            每个邮箱会生成一个全新邀请码并发送。
          </p>
        </div>

        {results && (
          <div className="max-h-48 overflow-auto rounded-md border border-border text-sm">
            {results.map((r) => (
              <div
                key={r.email}
                className="flex items-center justify-between border-b border-border px-3 py-1.5 last:border-b-0"
              >
                <span className="truncate">{r.email}</span>
                {r.success ? (
                  <span className="font-mono text-green-600">
                    {r.code || '—'}
                  </span>
                ) : (
                  <span className="text-destructive">{r.error || '失败'}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
